import React, { useMemo, useState } from 'react';

import {

  Box, Button, Chip, FormControl, IconButton, InputAdornment, MenuItem, OutlinedInput,

  Paper, Select, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Tooltip, Typography,

} from '@mui/material';

import AddRoundedIcon from '@mui/icons-material/AddRounded';

import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';

import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';

import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';



import { normalizeTargetCycles, TARGET_CYCLE_STATUS } from './targetCycleModel';



const scopes = ['scope1', 'scope2', 'scope3'];
const scopeLabels = { scope1: 'Scope 1', scope2: 'Scope 2', scope3: 'Scope 3' };



const isAvailable = (cycle, baselineYears = []) => {

  if (cycle.status === TARGET_CYCLE_STATUS.ARCHIVED) return 'Recorded';

  return baselineYears.some(year => Number(year) === Number(cycle.baseYear))

    ? 'Available' : 'Pending';

};



function ReductionSummary({ cycle, targets }) {

  const source = cycle.targets || targets || {};

  return (

    <Stack direction='row' flexWrap='wrap' gap={0.45}>

      {scopes.map(scope => {

        const target = source[scope];

        const value = target?.reductionPercent;

        return <Chip key={scope} label={`${scopeLabels[scope]}: ${Number.isFinite(Number(value)) ? `${Number(value)}%` : '—'}`} size='small' variant='outlined' />;

      })}

    </Stack>

  );

}



function HeaderWithHelp({ children, help }) {

  return (

    <Stack alignItems='center' component='span' direction='row' spacing={0.45}>

      <span>{children}</span>

      <Tooltip arrow title={help}>

        <HelpOutlineRoundedIcon color='action' sx={{ fontSize: 15 }} />

      </Tooltip>

    </Stack>

  );

}



export default function TargetConfigurationList({ baselineYears = [], canMutate, onAdd, onArchive, onDelete, onEdit, onView, targetCycles = [], targets = {}, t }) {

  const [query, setQuery] = useState('');

  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const cycles = useMemo(() => normalizeTargetCycles(targetCycles, targets), [targetCycles, targets]);

  const filteredCycles = cycles.filter(cycle => {

    const name = cycle.name || cycle.periodName || `${cycle.baseYear}–${cycle.targetYear} reduction plan`;

    const status = cycle.status === TARGET_CYCLE_STATUS.ACTIVE ? 'active' : 'archived';

    return (!query.trim() || name.toLowerCase().includes(query.trim().toLowerCase()))

      && (statusFilter === 'all' || status === statusFilter);

  });
  const pageCycles = filteredCycles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);



  return (

    <Paper className='climate-action-panel' variant='outlined' sx={{ borderTop: 4, borderTopColor: 'success.main', overflow: 'hidden' }}>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', p: { xs: 2, md: 2.5 } }}>

        <Stack alignItems={{ md: 'center' }} direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' spacing={1.5}>

          <Box>

            <Stack alignItems='center' direction='row' spacing={1}>

              <FlagOutlinedIcon sx={{ color: 'success.main' }} />

              <Typography variant='h6' sx={{ fontWeight: 750 }}>{t('climateAction.target.configuration.title')}</Typography>

            </Stack>

          </Box>

          {canMutate && (

            <Stack alignItems={{ xs: 'stretch', sm: 'center' }} direction={{ xs: 'column', sm: 'row' }} spacing={1}>

              <Button onClick={onAdd} startIcon={<AddRoundedIcon />} sx={{ whiteSpace: 'nowrap' }} variant='contained'>

                {t('climateAction.target.configuration.addPeriod')}

              </Button>

            </Stack>

          )}

        </Stack>

        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'minmax(300px, 1fr) 180px' }, mt: 2 }}>

          <OutlinedInput

            fullWidth

            onChange={event => setQuery(event.target.value)}

            placeholder={t('climateAction.target.configuration.searchPlaceholder')}

            size='small'

            startAdornment={<InputAdornment position='start'><SearchRoundedIcon fontSize='small' /></InputAdornment>}

            value={query}

          />

          <FormControl size='small'>

            <Select aria-label={t('climateAction.target.configuration.filterStatus')} onChange={event => setStatusFilter(event.target.value)} value={statusFilter}>

              <MenuItem value='all'>{t('climateAction.target.configuration.allStatuses')}</MenuItem>

              <MenuItem value='active'>{t('common.active')}</MenuItem>

              <MenuItem value='archived'>{t('climateAction.target.configuration.archived')}</MenuItem>

            </Select>

          </FormControl>

        </Box>

      </Box>



      <Box sx={{ overflowX: 'auto' }}>

        <Table size='small' sx={{ minWidth: 860 }}>

          <TableHead>

            <TableRow sx={{ bgcolor: 'background.default' }}>

              <TableCell sx={{ fontWeight: 750, width: 54 }}>#</TableCell>

              <TableCell sx={{ fontWeight: 750, width: 116 }}>{t('common.actions')}</TableCell>

              <TableCell sx={{ fontWeight: 750 }}>{t('climateAction.target.configuration.period')}</TableCell>

              <TableCell sx={{ fontWeight: 750 }}>{t('climateAction.target.configuration.fyPeriod')}</TableCell>

              <TableCell sx={{ fontWeight: 750 }}>{t('climateAction.target.configuration.reductionTargets')}</TableCell>

              <TableCell sx={{ fontWeight: 750 }}>

                <HeaderWithHelp help={t('climateAction.target.configuration.statusHelp')}>{t('common.status')}</HeaderWithHelp>

              </TableCell>

              <TableCell sx={{ fontWeight: 750 }}>

                <HeaderWithHelp help={t('climateAction.target.configuration.baselineHelp')}>{t('climateAction.target.configuration.baselineInventory')}</HeaderWithHelp>

              </TableCell>

            </TableRow>

          </TableHead>

          <TableBody>

            {pageCycles.map((cycle, rowIndex) => {

              const active = cycle.status === TARGET_CYCLE_STATUS.ACTIVE;

              const name = cycle.name || cycle.periodName || `${cycle.baseYear}–${cycle.targetYear} reduction plan`;

              const baseline = isAvailable(cycle, baselineYears);

              return (

                <TableRow hover key={cycle.id}>

                  <TableCell sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{rowIndex + 1}</TableCell>

                  <TableCell>

                    <Stack direction='row' spacing={0.25}>

                      <Tooltip title={t('climateAction.target.configuration.view')}>

                        <IconButton aria-label={`${t('climateAction.target.configuration.view')} ${name}`} onClick={() => onView(cycle)} size='small'>

                          <VisibilityOutlinedIcon fontSize='small' />

                        </IconButton>

                      </Tooltip>

                      {canMutate && active && (

                        <Tooltip title={t('climateAction.target.configuration.editActive')}>

                          <IconButton aria-label={`${t('climateAction.target.configuration.editActive')} ${name}`} onClick={() => onEdit(cycle)} size='small'>

                            <EditOutlinedIcon fontSize='small' />

                          </IconButton>

                        </Tooltip>

                      )}

                      {canMutate && active && (

                        <Tooltip title={t('climateAction.target.configuration.archive')}>

                          <IconButton aria-label={`${t('climateAction.target.configuration.archive')} ${name}`} onClick={() => onArchive(cycle)} size='small'>

                            <ArchiveOutlinedIcon fontSize='small' />

                          </IconButton>

                        </Tooltip>

                      )}

                      {canMutate && (

                        <Tooltip title={t('climateAction.target.configuration.delete')}>

                          <IconButton aria-label={`${t('climateAction.target.configuration.delete')} ${name}`} onClick={() => onDelete(cycle)} size='small'>

                            <DeleteOutlineRoundedIcon fontSize='small' />

                          </IconButton>

                        </Tooltip>

                      )}

                    </Stack>

                  </TableCell>

                  <TableCell>

                    <Typography sx={{ fontWeight: 700 }}>{name}</Typography>

                    {active && <Typography color='text.secondary' variant='caption'>{t('climateAction.target.configuration.usedByDashboard')}</Typography>}

                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{cycle.baseYear}–{cycle.targetYear}</TableCell>

                  <TableCell><ReductionSummary cycle={cycle} targets={targets} /></TableCell>

                  <TableCell>

                    <Chip color={active ? 'success' : 'default'} label={active ? t('common.active') : t('climateAction.target.configuration.archived')} size='small' variant={active ? 'outlined' : 'filled'} />

                  </TableCell>

                  <TableCell>

                    <Chip color={baseline === 'Pending' ? 'warning' : 'info'} label={t(`climateAction.target.configuration.${baseline.toLowerCase()}`)} size='small' variant='outlined' />

                  </TableCell>

                </TableRow>

              );

            })}

            {filteredCycles.length === 0 && (

              <TableRow><TableCell colSpan={7}><Typography color='text.secondary' sx={{ py: 3, textAlign: 'center' }}>{t('climateAction.target.configuration.noPeriods')}</Typography></TableCell></TableRow>

            )}

          </TableBody>

        </Table>

      </Box>
      <TablePagination
        component="div"
        count={filteredCycles.length}
        onPageChange={(event, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage={t('common.rowsPerPage')}
      />

    </Paper>

  );

}
